import type { ReactNode } from "react";
import {
  defaultChatModel,
  getAIProvider,
  managedAIProviderIdForModel,
  MANAGED_AI_PROVIDER_ID,
  resolveAIProviderId,
} from "../../shared/aiProviders";
import { config as environment } from "../environment";
import type { ScopedModel } from "../hooks/useScopedModel";
import { formatModelName } from "../lib/formatModelName";
import { Icon } from "./Icon";
import { MetaSep } from "./MetaSeparator";
import { ProviderLogo } from "./ProviderLogo";
import { Text } from "./Text";

function Subtitle({ text }: { text: ReactNode }) {
  return (
    <Text variant="text-small" color="var(--text-secondary)" ellipsis>
      {text}
    </Text>
  );
}

// The model picker shown in the composer and in preferences share one item
// list so they can't drift; only the selection side effect differs.
export function buildModelPickerItems({
  managedProvider,
  configs,
  tenantDefaultKey,
  effectiveKey,
  managedChatModel,
  managedDefaultChatModel,
  onSelectManaged,
  onSelectConfig,
}: {
  managedProvider: ScopedModel["managedProvider"];
  configs: ScopedModel["configs"];
  tenantDefaultKey: string | null;
  effectiveKey: string | null;
  managedChatModel: string | null;
  managedDefaultChatModel: string;
  onSelectManaged: (modelId: string, isDefault: boolean) => void;
  onSelectConfig: (configId: string, isDefault: boolean) => void;
}) {
  // Env-managed models aren't tenant configs, so surface each model directly.
  const managedItems = managedProvider
    ? environment.managedChatModels.map((modelId) => {
        const isDefault =
          tenantDefaultKey === MANAGED_AI_PROVIDER_ID &&
          modelId === managedDefaultChatModel;
        const selected =
          effectiveKey === MANAGED_AI_PROVIDER_ID &&
          managedChatModel === modelId;
        return {
          key: `${MANAGED_AI_PROVIDER_ID}:${modelId}`,
          type: "button" as const,
          left: (
            <ProviderLogo
              provider={managedAIProviderIdForModel(modelId)}
              size={16}
            />
          ),
          label: formatModelName(modelId),
          subtitle: (
            <Subtitle
              text={
                <>
                  Managed by 3B
                  {isDefault && (
                    <>
                      <MetaSep />
                      Default
                    </>
                  )}
                </>
              }
            />
          ),
          selected,
          right: selected ? (
            <Icon name="check" size={12} color="var(--text-secondary)" />
          ) : undefined,
          onClick: () => onSelectManaged(modelId, isDefault),
        };
      })
    : [];

  const configItems = configs
    .filter((config) => config.disabledAt == null)
    .map((config) => {
      const pid = resolveAIProviderId(config);
      const isDefault = tenantDefaultKey === config.id;
      const selected = effectiveKey === config.id;
      return {
        key: config.id,
        type: "button" as const,
        left: <ProviderLogo provider={pid} size={16} />,
        label: formatModelName(config.chatModel ?? defaultChatModel(pid)),
        subtitle: (
          <Subtitle
            text={
              <>
                {getAIProvider(pid).label}
                {isDefault && (
                  <>
                    <MetaSep />
                    Default
                  </>
                )}
              </>
            }
          />
        ),
        selected,
        right: selected ? (
          <Icon name="check" size={12} color="var(--text-secondary)" />
        ) : undefined,
        onClick: () => onSelectConfig(config.id, isDefault),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  return [...managedItems, ...configItems];
}
